import wait4Api from '@/api/wait4'

const state = {
  // LP表示データ
  pricingPlans: [],
  searchConditions: [],
  
  // フォーム状態
  applicationForm: {
    email: '',
    name: '',
    phone: '',
    plan_type: 'basic'
  },
  
  // アナリティクス
  sessionId: null,
  pageStartTime: null,
  
  // UI状態
  loading: false,
  showApplicationModal: false,
  showThankYouModal: false,
  
  // A/Bテスト
  copyVariant: 'short-copy-v1',
  hoverTracking: {},
  visionInteractions: []
}

const getters = {
  popularPlan: state => state.pricingPlans.find(p => p.is_popular),
  activeConditions: state => state.searchConditions.filter(c => c.is_active)
}

const mutations = {
  SET_LP_DATA(state, data) {
    state.pricingPlans = data.pricing_plans
    state.searchConditions = data.search_conditions
  },
  
  UPDATE_FORM(state, { field, value }) {
    state.applicationForm[field] = value
  },
  
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  
  TOGGLE_MODAL(state, modalName) {
    state[modalName] = !state[modalName]
  },
  
  SET_SESSION_ID(state, sessionId) {
    state.sessionId = sessionId
  },
  
  SET_PAGE_START_TIME(state, time) {
    state.pageStartTime = time
  },
  
  RESET_FORM(state) {
    state.applicationForm = {
      email: '',
      name: '',
      phone: '',
      plan_type: 'basic'
    }
  },
  
  SET_COPY_VARIANT(state, variant) {
    state.copyVariant = variant
  },
  
  TRACK_HOVER_START(state, { featureId, timestamp }) {
    state.hoverTracking[featureId] = timestamp
  },
  
  TRACK_HOVER_END(state, { featureId, timestamp, duration }) {
    const startTime = state.hoverTracking[featureId]
    if (startTime) {
      delete state.hoverTracking[featureId]
    }
  },
  
  ADD_VISION_INTERACTION(state, vision) {
    state.visionInteractions.push({
      vision,
      timestamp: Date.now()
    })
  }
}

const actions = {
  async fetchLPData({ commit }) {
    commit('SET_LOADING', true)
    try {
      const response = await wait4Api.getLPData()
      commit('SET_LP_DATA', response.data || response)
    } catch (error) {
      console.error('LP data fetch error:', error)
      // エラーの詳細をユーザーに通知することも検討
      // commit('SET_ERROR', error.message)
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async submitApplication({ state, commit, dispatch }) {
    commit('SET_LOADING', true)
    try {
      const data = {
        ...state.applicationForm,
        session_id: state.sessionId
      }
      await wait4Api.submitApplication(data)
      commit('TOGGLE_MODAL', 'showApplicationModal')
      commit('TOGGLE_MODAL', 'showThankYouModal')
      
      // コンバージョン追跡
      await dispatch('trackConversion')
      
      // フォームリセット
      commit('RESET_FORM')
    } catch (error) {
      console.error('Application submit error:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async trackAnalytics({ state }, data) {
    try {
      if (!state.sessionId) {
        console.warn('Session ID is not set, skipping analytics tracking')
        return
      }
      await wait4Api.trackAnalytics({
        session_id: state.sessionId,
        ...data
      })
    } catch (error) {
      console.error('Analytics tracking error:', error)
    }
  },
  
  async trackConversion({ state, dispatch }) {
    await dispatch('trackAnalytics', {
      page_name: 'application_form',
      action_type: 'conversion',
      action_details: { plan: state.applicationForm.plan_type }
    })
  },
  
  async trackPageView({ dispatch }, pageName) {
    await dispatch('trackAnalytics', {
      page_name: pageName,
      action_type: 'page_view',
      action_details: {}
    })
  },
  
  async trackClick({ dispatch }, { element, text }) {
    await dispatch('trackAnalytics', {
      page_name: 'landing_page',
      action_type: 'click',
      action_details: { element, text }
    })
  },
  
  async trackPageExit({ state, dispatch }) {
    const duration = Date.now() - state.pageStartTime
    await dispatch('trackAnalytics', {
      page_name: 'landing_page',
      action_type: 'page_exit',
      action_details: {
        duration: Math.floor(duration / 1000),
        exit_point: window.pageYOffset
      }
    })
  },
  
  async trackCopyVariant({ state, dispatch }) {
    await dispatch('trackAnalytics', {
      page_name: 'landing_page',
      action_type: 'copy_variant_view',
      action_details: {
        variant: state.copyVariant,
        timestamp: Date.now()
      }
    })
  },
  
  async trackPillClick({ dispatch }, pillText) {
    await dispatch('trackAnalytics', {
      page_name: 'landing_page',
      action_type: 'hero_pill_click',
      action_details: {
        pill_name: pillText,
        timestamp: Date.now()
      }
    })
  },
  
  async trackFeatureHover({ state, commit, dispatch }, { featureId, action }) {
    const timestamp = Date.now()
    
    if (action === 'start') {
      commit('TRACK_HOVER_START', { featureId, timestamp })
    } else {
      const startTime = state.hoverTracking[featureId]
      const duration = startTime ? timestamp - startTime : 0
      
      commit('TRACK_HOVER_END', { featureId, timestamp, duration })
      
      // ホバー時間をアナリティクスに送信
      if (duration > 0) {
        await dispatch('trackAnalytics', {
          page_name: 'landing_page',
          action_type: 'feature_hover',
          action_details: {
            feature_id: featureId,
            duration_ms: duration
          }
        })
      }
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}


// WEBPACK FOOTER //
// ./src/store/modules/wait4.js